import BaseTool from './BaseTool.js';
import Transform from '../../math/Transform.js';
import Material from '../../materials/Material.js';

// Face opposite mapping for bidirectional extension
const OPPOSITE_FACE = {
  right: 'left',
  left: 'right',
  top: 'bottom',
  bottom: 'top',
  back: 'front',
  front: 'back'
};

/**
 * Tool for resizing cuboids by dragging faces
 * Minecraft-style face extension with camera-aware direction handling
 * Supports bidirectional extension - dragging past origin extends the opposite face
 */
export default class ExtendTool extends BaseTool {
  constructor(editor) {
    super(editor);

    // Drag state
    this.targetCuboid = null;
    this.targetFace = null;
    this.originalDimensions = null;
    this.dragStartX = 0;
    this.dragStartY = 0;
    this.lastDelta = 0;
  }

  get name() {
    return 'extend';
  }

  /**
   * Handle mouse down - start extending if clicking a face
   * @param {MouseEvent} event
   */
  onMouseDown(event) {
    if (event.button !== 0) return; // Left click only

    const faceEl = event.target.closest('.face');
    if (!faceEl || faceEl.closest('.ghost-preview')) return;

    const cuboid = this.getCuboidFromElement(faceEl);
    if (!cuboid) return;

    const face = this.getFaceType(faceEl);
    if (!face) return;

    // Start extending
    this.targetCuboid = cuboid;
    this.targetFace = face;
    this.originalDimensions = {
      x: cuboid.x,
      y: cuboid.y,
      z: cuboid.z,
      width: cuboid.width,
      height: cuboid.height,
      depth: cuboid.depth
    };
    // Preserve texture origin for world-stationary resize behavior
    // This keeps textures anchored during extension operations
    this.originalTextureOrigin = { ...cuboid.textureOrigin };
    this.dragStartX = event.clientX;
    this.dragStartY = event.clientY;
    this.lastDelta = 0;
    this.isDragging = true;

    // Show initial ghost at original position (uses default ghost appearance)
    // Note: We don't show cuboid materials because triplanar textures
    // require position-dependent UV calculations that are complex to preview
    this.ghostPreview.show(
      cuboid.x, cuboid.y, cuboid.z,
      cuboid.width, cuboid.height, cuboid.depth,
      true
    );
  }

  /**
   * Handle mouse move - update ghost preview
   * @param {MouseEvent} event
   */
  onMouseMove(event) {
    if (!this.isDragging || !this.targetCuboid) return;

    this.updateGhostDimensions(event);
  }

  /**
   * Handle mouse up - finalize resize if valid
   * @param {MouseEvent} event
   */
  onMouseUp(event) {
    if (!this.isDragging || !this.targetCuboid) {
      this.cancelDrag();
      return;
    }

    const newDims = this.calculateNewDimensions(event);
    if (!newDims) {
      this.cancelDrag();
      return;
    }

    const cuboid = this.targetCuboid;
    const key = cuboid.getKey();

    // Check if dimensions actually changed
    const hasChanged = (
      newDims.x !== this.originalDimensions.x ||
      newDims.y !== this.originalDimensions.y ||
      newDims.z !== this.originalDimensions.z ||
      newDims.width !== this.originalDimensions.width ||
      newDims.height !== this.originalDimensions.height ||
      newDims.depth !== this.originalDimensions.depth
    );

    // Check validity
    const isValid = hasChanged && this.collisionManager.isValidPlacement(
      newDims.x, newDims.y, newDims.z,
      newDims.width, newDims.height, newDims.depth,
      key
    );

    if (isValid) {
      // Snapshot impostors sitting on this cuboid before resizing
      const impostorsOnCuboid = this.world.getImpostorsOnCuboid(cuboid);

      // Save materials
      const materials = {};
      ['top', 'bottom', 'front', 'back', 'left', 'right'].forEach(face => {
        materials[face] = cuboid.materials[face];
      });

      // Save group membership for each face before removal
      const faceGroups = {};
      const groupManager = this.world.materialGroupManager;
      if (groupManager) {
        ['top', 'bottom', 'front', 'back', 'left', 'right'].forEach(face => {
          const group = groupManager.getGroupForFace(key, face);
          if (group) {
            faceGroups[face] = group.id;
          }
        });
      }

      // Remove old cuboid
      this.world.removeCuboid(
        this.originalDimensions.x,
        this.originalDimensions.y,
        this.originalDimensions.z,
        this.originalDimensions.width,
        this.originalDimensions.height,
        this.originalDimensions.depth
      );

      // Update impostor Y positions to new surface BEFORE addCuboid validates them
      if (impostorsOnCuboid.length > 0) {
        const newTopY = newDims.y + newDims.height;
        for (const imp of impostorsOnCuboid) {
          imp.y = newTopY;
        }
      }

      // Add resized cuboid (validateAllImpostors runs inside, checking edge margins,
      // wall intersections, and floor validity for ALL impostors in the world)
      this.world.addCuboid(
        newDims.x, newDims.y, newDims.z,
        newDims.width, newDims.height, newDims.depth,
        materials,
        this.originalTextureOrigin
      );

      // Restore group membership with new cuboid key
      const newKey = `${newDims.x},${newDims.y},${newDims.z},${newDims.width},${newDims.height},${newDims.depth}`;
      const restoredGroups = new Set();
      if (groupManager && Object.keys(faceGroups).length > 0) {
        Object.entries(faceGroups).forEach(([face, groupId]) => {
          groupManager.assignFacesToGroup(groupId, [{ cuboidKey: newKey, face }]);
          restoredGroups.add(groupId);
        });

        // Re-apply materials for groups that were restored (fixes triplanar UV for new dimensions)
        restoredGroups.forEach(groupId => {
          groupManager.applyGroupMaterial(groupId);
        });
      }

      // Update face selection to use new key
      this.updateSelectionKey(key, newKey);

      // Notify UI if impostors were on the affected cuboid
      if (impostorsOnCuboid.length > 0) {
        window.dispatchEvent(new CustomEvent('impostors-changed'));
      }

      // Record undo action
      if (this.editor.undoManager) {
        const oldDims = { ...this.originalDimensions };
        const nDims = { x: newDims.x, y: newDims.y, z: newDims.z, width: newDims.width, height: newDims.height, depth: newDims.depth };
        const oldK = key;
        const newK = newKey;
        const texOrigin = this.originalTextureOrigin ? { ...this.originalTextureOrigin } : null;
        const matSnap = {};
        for (const face of ['top', 'bottom', 'front', 'back', 'left', 'right']) {
          if (materials[face]) matSnap[face] = materials[face].toJSON();
        }
        const groupSnap = { ...faceGroups };
        const impYChanges = impostorsOnCuboid.map(imp => ({
          id: imp.id,
          newY: nDims.y + nDims.height,
          oldY: oldDims.y + oldDims.height
        }));
        const world = this.world;
        this.editor.undoManager.push({
          description: 'Extend cuboid',
          undo: () => {
            const gm = world.materialGroupManager;
            if (gm) {
              for (const face of ['top', 'bottom', 'front', 'back', 'left', 'right']) {
                const group = gm.getGroupForFace(newK, face);
                if (group) gm.removeFacesFromGroup(group.id, [{ cuboidKey: newK, face }]);
              }
            }
            world.removeCuboid(nDims.x, nDims.y, nDims.z, nDims.width, nDims.height, nDims.depth);
            for (const ic of impYChanges) {
              const imp = world.impostors.get(ic.id);
              if (imp) imp.y = ic.oldY;
            }
            const restored = {};
            for (const [face, data] of Object.entries(matSnap)) restored[face] = Material.fromJSON(data);
            world.addCuboid(oldDims.x, oldDims.y, oldDims.z, oldDims.width, oldDims.height, oldDims.depth, restored, texOrigin);
            if (gm) {
              const groups = new Set();
              for (const [face, groupId] of Object.entries(groupSnap)) {
                gm.assignFacesToGroup(groupId, [{ cuboidKey: oldK, face }]);
                groups.add(groupId);
              }
              groups.forEach(gid => gm.applyGroupMaterial(gid));
            }
            world.update();
          },
          redo: () => {
            const gm = world.materialGroupManager;
            if (gm) {
              for (const face of ['top', 'bottom', 'front', 'back', 'left', 'right']) {
                const group = gm.getGroupForFace(oldK, face);
                if (group) gm.removeFacesFromGroup(group.id, [{ cuboidKey: oldK, face }]);
              }
            }
            world.removeCuboid(oldDims.x, oldDims.y, oldDims.z, oldDims.width, oldDims.height, oldDims.depth);
            for (const ic of impYChanges) {
              const imp = world.impostors.get(ic.id);
              if (imp) imp.y = ic.newY;
            }
            const restored = {};
            for (const [face, data] of Object.entries(matSnap)) restored[face] = Material.fromJSON(data);
            world.addCuboid(nDims.x, nDims.y, nDims.z, nDims.width, nDims.height, nDims.depth, restored);
            if (gm) {
              const groups = new Set();
              for (const [face, groupId] of Object.entries(groupSnap)) {
                gm.assignFacesToGroup(groupId, [{ cuboidKey: newK, face }]);
                groups.add(groupId);
              }
              groups.forEach(gid => gm.applyGroupMaterial(gid));
            }
            world.update();
          }
        });
      }

      // Update world
      this.world.update();
    }

    this.cancelDrag();
  }

  /**
   * Calculate drag delta based on face type
   * Uses screen-space projection of the face normal for intuitive "pull" feeling
   * @param {MouseEvent} event
   * @return {number} Delta in grid units
   */
  calculateDelta(event) {
    // Get screen delta
    const dx = event.clientX - this.dragStartX;
    const dy = event.clientY - this.dragStartY;

    // Pixels per grid unit for Y axis (vertical on screen)
    const pixelsPerUnitY = this.camera.CUBE_SIZE * this.camera.zoomLevel;

    // For top/bottom faces, just use vertical mouse movement
    if (this.targetFace === 'top') {
      return Math.round(-dy / pixelsPerUnitY);
    }
    if (this.targetFace === 'bottom') {
      return Math.round(dy / pixelsPerUnitY);
    }

    // For side faces, project the face normal to screen space
    // Then project mouse movement onto that screen direction
    const angleRad = this.camera.angle * Math.PI / 180;
    const cos = Math.cos(angleRad);
    const sin = Math.sin(angleRad);
    const yScale = Transform.calculateYScale(this.camera.skewAngle);

    // Face normals in world space and their screen projections
    let screenNormalX, screenNormalY;

    switch (this.targetFace) {
      case 'right':
        // Normal: (1, 0, 0)
        screenNormalX = cos;
        screenNormalY = sin * yScale;
        break;
      case 'left':
        // Normal: (-1, 0, 0)
        screenNormalX = -cos;
        screenNormalY = -sin * yScale;
        break;
      case 'back':
        // Normal: (0, 0, 1) but in our coord system Z is negated for screen
        screenNormalX = sin;
        screenNormalY = -cos * yScale;
        break;
      case 'front':
        // Normal: (0, 0, -1)
        screenNormalX = -sin;
        screenNormalY = cos * yScale;
        break;
      default:
        return 0;
    }

    // Calculate how many pixels one grid unit appears as on screen
    const len = Math.sqrt(screenNormalX * screenNormalX + screenNormalY * screenNormalY);
    if (len < 0.001) return 0;

    // Pixels per grid unit along this face's normal direction
    const pixelsPerUnit = len * this.camera.CUBE_SIZE * this.camera.zoomLevel;

    // Normalize for direction only
    const dirX = screenNormalX / len;
    const dirY = screenNormalY / len;

    // Project mouse movement onto screen normal direction
    const projection = dx * dirX + dy * dirY;

    // Convert pixel movement to grid units (1:1 feel)
    return Math.round(projection / pixelsPerUnit);
  }

  /**
   * Calculate new dimensions based on drag delta
   * Supports bidirectional extension - when dimension would go below 1,
   * starts extending the opposite face instead
   * @param {MouseEvent} event
   * @return {Object|null} New dimensions {x, y, z, width, height, depth}
   */
  calculateNewDimensions(event) {
    if (!this.originalDimensions || !this.targetFace) return null;

    const delta = this.calculateDelta(event);
    const { x, y, z, width, height, depth } = this.originalDimensions;

    // Calculate new dimensions based on face
    // Each case handles bidirectional extension when dimension would go below 1
    switch (this.targetFace) {
      case 'top':
        // Extend upward - increase height
        if (height + delta >= 1) {
          return { x, y, z, width, height: height + delta, depth };
        } else {
          // Bidirectional: extend bottom instead
          const overflow = 1 - height - delta;
          return { x, y: y - overflow, z, width, height: 1 + overflow, depth };
        }

      case 'bottom': {
        // Extend downward - decrease y and increase height
        if (height + delta >= 1) {
          const newHeight = height + delta;
          const diff = newHeight - height;
          return { x, y: y - diff, z, width, height: newHeight, depth };
        } else {
          // Bidirectional: extend top instead
          const overflow = 1 - height - delta;
          return { x, y: y + height - 1, z, width, height: 1 + overflow, depth };
        }
      }

      case 'right':
        // Extend right - increase width
        if (width + delta >= 1) {
          return { x, y, z, width: width + delta, height, depth };
        } else {
          // Bidirectional: extend left instead
          const overflow = 1 - width - delta;
          return { x: x - overflow, y, z, width: 1 + overflow, height, depth };
        }

      case 'left': {
        // Extend left - decrease x and increase width
        if (width + delta >= 1) {
          const newWidth = width + delta;
          const diff = newWidth - width;
          return { x: x - diff, y, z, width: newWidth, height, depth };
        } else {
          // Bidirectional: extend right instead
          const overflow = 1 - width - delta;
          return { x: x + width - 1, y, z, width: 1 + overflow, height, depth };
        }
      }

      case 'back':
        // Extend backward (positive Z) - increase depth
        if (depth + delta >= 1) {
          return { x, y, z, width, height, depth: depth + delta };
        } else {
          // Bidirectional: extend front instead
          const overflow = 1 - depth - delta;
          return { x, y, z: z - overflow, width, height, depth: 1 + overflow };
        }

      case 'front': {
        // Extend forward (negative Z) - decrease z and increase depth
        if (depth + delta >= 1) {
          const newDepth = depth + delta;
          const diff = newDepth - depth;
          return { x, y, z: z - diff, width, height, depth: newDepth };
        } else {
          // Bidirectional: extend back instead
          const overflow = 1 - depth - delta;
          return { x, y, z: z + depth - 1, width, height, depth: 1 + overflow };
        }
      }

      default:
        return null;
    }
  }

  /**
   * Update ghost preview with current dimensions
   * @param {MouseEvent} event
   */
  updateGhostDimensions(event) {
    const newDims = this.calculateNewDimensions(event);
    if (!newDims) return;

    const key = this.targetCuboid.getKey();

    // Check validity
    const isValid = this.collisionManager.isValidPlacement(
      newDims.x, newDims.y, newDims.z,
      newDims.width, newDims.height, newDims.depth,
      key
    );

    // Show ghost
    this.ghostPreview.show(
      newDims.x, newDims.y, newDims.z,
      newDims.width, newDims.height, newDims.depth,
      isValid
    );
  }

  /**
   * Cancel the current drag operation
   */
  cancelDrag() {
    this.targetCuboid = null;
    this.targetFace = null;
    this.originalDimensions = null;
    this.originalTextureOrigin = null;
    this.isDragging = false;
    this.lastDelta = 0;
    this.ghostPreview.hide();
  }

  deactivate() {
    this.cancelDrag();
    super.deactivate();
  }
}
